import React, { useEffect, useState } from 'react';
import { get, post, del } from '../api';

const box = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, marginBottom: 16 };
const mono = { fontFamily: "'JetBrains Mono', monospace", fontSize: 12 };

function formatDate(value) {
  return value ? new Date(value).toLocaleString() : '—';
}

// Assistant keys let Claude, ChatGPT or Adaptive draft and send ECC emails
// after Casey says "send" in the chat. Other changes need Casey's sign-in.
export default function AssistantKeys() {
  const [me, setMe] = useState(null);
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState('');
  const [newKey, setNewKey] = useState(null);
  const [message, setMessage] = useState('');

  const load = () => get('/api/auth/agent-keys').then(r => setKeys(r.keys || [])).catch(err => setMessage(err.message));

  useEffect(() => {
    get('/api/auth/me').then(r => {
      setMe(r);
      if (r.actor === 'admin') load();
    }).catch(() => setMe({ actor: 'anonymous' }));
  }, []);

  const create = async (event) => {
    event.preventDefault();
    setMessage('');
    try {
      const created = await post('/api/auth/agent-keys', { name });
      setNewKey(created);
      setName('');
      load();
    } catch (err) {
      setMessage(err.message);
    }
  };

  const revoke = async (key) => {
    setMessage('');
    try {
      await del(`/api/auth/agent-keys/${key.id}`);
      setMessage(`Revoked ${key.name}.`);
      load();
    } catch (err) {
      setMessage(err.message);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(newKey.key);
      setMessage('Key copied.');
    } catch (err) {
      setMessage('Select the key and copy it manually.');
    }
  };

  if (!me) return null;
  if (me.actor !== 'admin') {
    return (
      <div style={box}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Assistant keys</div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>Sign in to manage the keys Claude and ChatGPT use to work in ECC.</div>
        <a href="/login" style={{ color: 'var(--accent, #5cc49c)' }}>Sign in</a>
      </div>
    );
  }

  return (
    <div style={box}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>Assistant keys</div>
        <form method="post" action="/auth/logout">
          <button type="submit" style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--muted)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>
            Sign out {me.name}
          </button>
        </form>
      </div>
      <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 14 }}>
        Assistants can draft, test and send emails once you say "send" in the chat. Every action is logged with the assistant's name.
      </div>

      <form onSubmit={create} style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <input id="assistant-key-name" value={name} onChange={e => setName(e.target.value)} placeholder="Claude, ChatGPT…"
          style={{ flex: '1 1 180px', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' }} />
        <button type="submit" disabled={!name.trim()} style={{ padding: '8px 14px', borderRadius: 6, border: 0, background: 'var(--accent, #5cc49c)', color: '#0f1412', fontWeight: 600, cursor: 'pointer' }}>
          Create key
        </button>
      </form>

      {newKey && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 14 }}>
          <div style={{ fontSize: 13, marginBottom: 6 }}>Key for <strong>{newKey.name}</strong>. Copy it now; ECC can't show it again.</div>
          <div style={{ ...mono, wordBreak: 'break-all', marginBottom: 8 }}>{newKey.key}</div>
          <button type="button" onClick={copy} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'none', color: 'var(--text)', cursor: 'pointer' }}>Copy</button>
        </div>
      )}

      {message && <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 10 }}>{message}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {keys.length === 0 && <div style={{ fontSize: 13, color: 'var(--muted)' }}>No assistant keys yet.</div>}
        {keys.map(key => (
          <div key={key.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', fontSize: 13, opacity: key.revoked_at ? 0.5 : 1 }}>
            <div style={{ minWidth: 0 }}>
              <strong>{key.name}</strong> <span style={mono}>{key.key_prefix}…</span>
              <div style={{ color: 'var(--muted)', fontSize: 12 }}>
                Last used {formatDate(key.last_used_at)}{key.revoked_at ? ` · revoked ${formatDate(key.revoked_at)}` : ''}
              </div>
            </div>
            {!key.revoked_at && (
              <button type="button" onClick={() => revoke(key)} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'none', color: 'var(--red, #ef4444)', cursor: 'pointer' }}>
                Revoke
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
